/**
 * Utilitaires pour normaliser les types Elasticsearch vers les types de filtres
 * utilisés dans le modal "plus de filtres"
 */

/**
 * Types de filtres supportés dans le modal
 * Note: "keyword" a été remplacé par "text" pour transparence utilisateur
 */
export type FilterType = "boolean" | "date" | "text" | "float" | "number";

/**
 * Normalise un type Elasticsearch vers un type de filtre
 * 
 * Types ES supportés :
 * - boolean → boolean
 * - date, date_nanos, datetime → date
 * - keyword, text, string → text (keyword masqué pour l'utilisateur)
 * - float, double, half_float, scaled_float → float
 * - long, integer, short, byte, number → number
 */
export const normalizeElasticsearchTypeToFilterType = (
  esType: string | undefined
): FilterType => {
  if (!esType) {
    return "text"; // Par défaut
  }

  const normalizedType = esType.toLowerCase().trim();

  // Type boolean
  if (normalizedType === "boolean" || normalizedType === "bool") {
    return "boolean";
  }

  // Types de date
  if (
    normalizedType === "date" ||
    normalizedType === "date_nanos" ||
    normalizedType === "datetime"
  ) {
    return "date";
  }

  // Types de texte → text (keyword est un détail d'implémentation ES)
  if (
    normalizedType === "keyword" ||
    normalizedType === "text" ||
    normalizedType === "string"
  ) {
    return "text";
  }

  // Types numériques flottants → float
  if (
    normalizedType === "float" ||
    normalizedType === "double" ||
    normalizedType === "half_float" ||
    normalizedType === "scaled_float"
  ) {
    return "float";
  }

  // Types numériques entiers → number
  if (
    normalizedType === "long" ||
    normalizedType === "integer" ||
    normalizedType === "int" ||
    normalizedType === "short" ||
    normalizedType === "byte" ||
    normalizedType === "number"
  ) {
    return "number";
  }

  // Par défaut, considérer comme text
  return "text";
};

/**
 * Vérifie si un type ES est un type numérique (float ou number)
 */
export const isNumericType = (esType: string | undefined): boolean => {
  const filterType = normalizeElasticsearchTypeToFilterType(esType);
  return filterType === "float" || filterType === "number";
};

/**
 * Vérifie si un type ES est un type de date
 */
export const isDateType = (esType: string | undefined): boolean => {
  return normalizeElasticsearchTypeToFilterType(esType) === "date";
};

/**
 * Vérifie si un type ES est un type boolean
 */
export const isBooleanType = (esType: string | undefined): boolean => {
  return normalizeElasticsearchTypeToFilterType(esType) === "boolean";
};

/**
 * Vérifie si un type ES est un type text (anciennement keyword/text)
 */
export const isTextType = (esType: string | undefined): boolean => {
  return normalizeElasticsearchTypeToFilterType(esType) === "text";
};

